import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import InfoIcon from 'assets/info-icon.svg'
import DeliveryModal from './DeliveryModal'
import priceStr from 'utils/priceString'
import useAccount from 'hooks/account/useAccount'
import useTranslations from 'hooks/useTranslations'
import * as Prices from 'theme/atoms/Price'
import usePromotionCodes from 'hooks/account/cart/usePromotionCodes'

export type Props = {
  positionPrice: number // sum of all items
  totalPrice: number // sum of all items + delivery + tax
  deliveryReduction?: number // reduction of delivery costs
  tax: number
  taxRate: number
  ecoTax?: { ecoTaxNet: number; ecoTaxGross: number }
  deliveryCost: number
  specialDelivery: boolean
  loading?: boolean
}

export default function CartPriceTable(props: Props) {
  const t = useTranslations<'molecules-account-CartPriceTable'>()
  const [showInfo, setShowInfo] = React.useState(false)
  const account = useAccount()
  const promotions = usePromotionCodes()
  const [reduction, setReduction] = React.useState(0)
  React.useEffect(() => {
    const reduction = promotions.items.reduce(
      (acc, item) => acc + item.reduction,
      0
    )
    setReduction(reduction)
  }, [promotions.items])
  return (
    <Wrapper
      className="CartPriceTable"
      data-cy-ctx="molecules/account/CartPriceTable"
    >
      {promotions.items.length > 0 && (
        <>
          <div className="line bold" data-cy-state="has-promotions">
            <div className="label">{t('cartpricetable_cart')}</div>
            <div className="value">
              {props.loading
                ? ''
                : priceStr((props.positionPrice - reduction).toFixed(2))}
            </div>
          </div>
          {promotions.items.map((promoItem, i) => (
            <div className="line" key={promoItem.cartItemId || i}>
              <div className="label">{t('cartpricetable_deliveryvoucher')}</div>
              <div className="value">{priceStr(promoItem.reduction)}</div>
            </div>
          ))}
        </>
      )}
      <div className="line bold">
        <div className="label">{t('cartpricetable_subtotal')}</div>
        <div className="value">
          {props.loading ? '' : priceStr(props.positionPrice)}
        </div>
      </div>
      <div className="line">
        <div className="label">
          {t('cartpricetable_deliverycost')}
          <InfoIcon
            onClick={() => setShowInfo(true)}
            data-cy-handle="delivery-info-icon"
          />
        </div>
        <div className="value">
          {props.loading ? '' : priceStr(props.deliveryCost)}
        </div>
      </div>
      {!!props.deliveryReduction && (
        <div className="line green" data-cy-state="has-delivery-reduction">
          <div className="label">{t('cartpricetable_deliveryvoucher')}</div>
          <div className="value">-{priceStr(props.deliveryReduction)}</div>
        </div>
      )}
      {account.channel === 'b2b' && (
        <div className="line" data-cy-state="show-tax">
          <div className="label">
            {t('cartpricetable_plus_vat', { vatPercentage: props.taxRate })}
          </div>
          <div className="value">
            {props.loading ? '' : priceStr(props.tax)}
          </div>
        </div>
      )}
      {props.ecoTax &&
        (props.ecoTax.ecoTaxGross > 0 || props.ecoTax.ecoTaxNet > 0) && (
          <div className="line" data-cy-state="eco-tax">
            <div className="label">{t('cartpricetable_eco_tax')}</div>
            <div className="value">
              <Prices.EcoTaxSum prices={props.ecoTax} />
            </div>
          </div>
        )}
      <hr />
      <div className="line bold">
        <div className="label">
          {t('cartpricetable_total', {
            VATINFO: '_',
            __transformers: {
              VATINFO: (_, vars) => (
                <span>{account.channel === 'b2b' ? '' : vars.VATINFO_B2C}</span>
              )
            }
          })}
        </div>
        <div className="value">
          {props.loading ? '' : priceStr(props.totalPrice)}
        </div>
      </div>

      {showInfo && (
        <DeliveryModal
          specialDelivery={props.specialDelivery}
          data-cy-state="show-delivery-modal"
          onClose={() => setShowInfo(false)}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing('xs')};

  ${theme.ty('r-s')}

  @media (min-width: ${theme.ms.MD}px) {
    ${theme.ty('r-base')}
  }

  > .line {
    display: flex;
    justify-content: space-between;
    &.bold {
      font-weight: bold;
    }
    &.green {
      font-weight: bold;
      color: rgba(0, 102, 68, 1);
    }

    > .label {
      display: flex;
      align-items: center;
      > svg {
        width: 15px;
        height: 15px;
        cursor: pointer;
        margin-left: 6px;
        circle {
          fill: ${theme.colors.b0};
        }
      }
    }
  }

  > hr {
    width: 100%;
    border-top: 1px solid ${theme.colors.shade.b5};
  }
`
